import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";
import { Loader2, X } from "lucide-react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";

const COLORS = ["#d8b854", "#ffffff", "#4ade80", "#60a5fa", "#f87171"];

const STAT_ROWS = [
  { key: "pts", label: "PPG" },
  { key: "reb", label: "RPG" },
  { key: "ast", label: "APG" },
  { key: "stl", label: "SPG" },
  { key: "blk", label: "BPG" },
  { key: "turnover", label: "TO" },
  { key: "fg_pct", label: "FG%", isPct: true },
  { key: "fg3_pct", label: "3P%", isPct: true },
  { key: "ft_pct", label: "FT%", isPct: true },
];

function fmtVal(v, isPct) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return isPct ? `${(n * 100).toFixed(1)}%` : n.toFixed(1);
}
function toNum(v) {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

export default function ComparePage() {
  const [params] = useSearchParams();
  const ids = useMemo(() => (params.get("ids") || "").split(",").filter(Boolean), [params]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ids.length < 2) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const d = await api.compare(ids);
        setData(d);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      } finally { setLoading(false); }
    })();
  }, [ids]);

  const chartData = useMemo(() => {
    if (!data?.players) return [];
    return STAT_ROWS.filter(r => !r.isPct).map(r => {
      const row = { stat: r.label };
      data.players.forEach(p => {
        row[p.name] = toNum(p.season_averages?.[r.key]);
      });
      return row;
    });
  }, [data]);

  const radarData = useMemo(() => {
    if (!data?.players) return [];
    // Normalize each stat to 0-100 relative to max across players for a radar
    const maxByStat = {};
    STAT_ROWS.forEach(r => {
      maxByStat[r.key] = Math.max(1, ...data.players.map(p => toNum(p.season_averages?.[r.key]) * (r.isPct ? 100 : 1)));
    });
    return STAT_ROWS.map(r => {
      const row = { stat: r.label };
      data.players.forEach(p => {
        const raw = toNum(p.season_averages?.[r.key]) * (r.isPct ? 100 : 1);
        row[p.name] = Math.round((raw / maxByStat[r.key]) * 100);
      });
      return row;
    });
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-[1400px] mx-auto px-6 py-24 flex items-center justify-center text-[#a0a0a0]" data-testid="compare-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading comparison…
        </div>
      </div>
    );
  }

  if (ids.length < 2 || error || !data) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-[1400px] mx-auto px-6 py-24 text-center">
          <div className="font-heading text-4xl uppercase text-white mb-2">Pick players to compare</div>
          <p className="text-[#a0a0a0] mb-6">{error || "Select 2 to 5 players from your watchlist to compare."}</p>
          <Link to="/" className="text-[var(--org-accent)] uppercase text-xs tracking-[0.25em]">← Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="border-b border-white/10" data-testid="compare-hero">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[var(--org-accent)] mb-4">Compare · {data.players.length} Players</div>
          <h1 className="font-heading text-5xl lg:text-6xl leading-[0.9] tracking-tight text-white uppercase">Head to Head</h1>
          <div className="mt-6 flex flex-wrap gap-3">
            {data.players.map((p, i) => (
              <Link
                key={p.id}
                to={`/home/${p.id}`}
                className="flex items-center gap-3 px-4 py-2 border border-white/10 bg-[#1c1c1c] hover:border-[var(--org-accent)]"
                style={{ transition: "border-color 0.2s ease" }}
                data-testid={`compare-chip-${i}`}
              >
                <span className="w-3 h-3 border border-white/20" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="font-heading text-lg uppercase text-white">{p.name}</span>
                <span className="text-[10px] uppercase tracking-widest text-[#a0a0a0]">{p.team}</span>
                <span className={`text-[9px] px-1.5 py-0.5 font-bold tracking-widest ${p.league === "NBA" ? "bg-[var(--org-accent)] text-[#121212]" : "border border-[var(--org-accent)] text-[var(--org-accent)]"}`}>{p.league}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-10">

        {/* Grouped bar chart */}
        <section data-testid="compare-bar">
          <SectionHead label="Averages · Bar Chart" />
          <div className="border border-white/10 bg-[#1c1c1c] p-4 h-[420px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid stroke="#333333" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="stat" stroke="#757575" fontSize={12} tick={{ fill: "#a0a0a0" }} />
                <YAxis stroke="#757575" fontSize={12} tick={{ fill: "#a0a0a0" }} />
                <Tooltip contentStyle={{ background: "#232323", border: "1px solid #d8b854", borderRadius: 0, color: "#fff", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#a0a0a0", textTransform: "uppercase", letterSpacing: "0.2em" }} />
                {data.players.map((p, i) => (
                  <Bar key={p.id} dataKey={p.name} fill={COLORS[i % COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Radar chart (normalized) */}
        {data.players.length >= 2 && (
          <section data-testid="compare-radar">
            <SectionHead label="Profile · Radar (normalized to leader = 100)" />
            <div className="border border-white/10 bg-[#1c1c1c] p-4 h-[440px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#3c3c3c" />
                  <PolarAngleAxis dataKey="stat" tick={{ fill: "#a0a0a0", fontSize: 11 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: "#757575", fontSize: 10 }} stroke="#3c3c3c" />
                  {data.players.map((p, i) => (
                    <Radar key={p.id} name={p.name} dataKey={p.name} stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.15} />
                  ))}
                  <Legend wrapperStyle={{ fontSize: 11, color: "#a0a0a0", textTransform: "uppercase", letterSpacing: "0.2em" }} />
                  <Tooltip contentStyle={{ background: "#232323", border: "1px solid #d8b854", borderRadius: 0, color: "#fff", fontSize: 12 }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}

        {/* Comparison table */}
        <section data-testid="compare-table">
          <SectionHead label="Season Averages Side-by-Side" />
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#101010] text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
                <tr>
                  <th className="text-left px-4 py-3 border-b border-white/10 font-bold">Stat</th>
                  {data.players.map((p, i) => (
                    <th key={p.id} className="text-left px-4 py-3 border-b border-white/10 font-bold" style={{ color: COLORS[i % COLORS.length] }}>
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="font-mono">
                {STAT_ROWS.map((r, ri) => {
                  const vals = data.players.map(p => toNum(p.season_averages?.[r.key]));
                  const maxV = Math.max(...vals);
                  return (
                    <tr key={r.key} className={ri % 2 === 0 ? "bg-[#1c1c1c]" : "bg-[#101010]"} data-testid={`compare-row-${r.key}`}>
                      <td className="px-4 py-3 border-b border-white/5 text-[#a0a0a0] uppercase text-[11px] tracking-[0.2em]">{r.label}</td>
                      {data.players.map((p, i) => {
                        const raw = p.season_averages?.[r.key];
                        const isLeader = toNum(raw) === maxV && maxV > 0;
                        return (
                          <td
                            key={p.id}
                            className={`px-4 py-3 border-b border-white/5 ${isLeader ? "font-bold text-white" : "text-[#a0a0a0]"}`}
                            data-testid={`compare-cell-${r.key}-${i}`}
                          >
                            {fmtVal(raw, r.isPct)}
                            {isLeader && <span className="ml-2 text-[9px] text-[var(--org-accent)] tracking-widest">LEAD</span>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

function SectionHead({ label }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#a0a0a0] font-bold">{label}</div>
      <div className="flex-1 h-px bg-white/10" />
    </div>
  );
}
